import React, { useEffect, useState } from 'react'

import { Box, Grid, Typography } from '@mui/material'
import ButtonModal from '@react/components/ButtonModal'
import { SectionHeader } from '@react/components/common/SectionHeader'
import { TableHeaderItem } from '@react/components/TableHeaderItem'
import { theme } from '@react/components/theme'
import { AddressPill } from '@react/components/addresses/AddressPill' // Add this import
import { timestampToUTCDateTimeString } from '@shared/conversions'

import { fetchBalance, fetchPoolDynamicDeposits, scanPoolsForEvents } from '@react/fetchers/PoolFetchers'
import { useOtChainState, useOtSelector } from '@react/redux/store'

import { CompanyProfileAddressItem } from '@react/admin/CompanyProfileAddressItem'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import { DynamicDepositStatusEnum, RoleEnum } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'
import { DynamicDepositActivityCTO } from '@shared/PoolEventCTO'
import { ETH_ADDR_ZERO, EthAddr } from '@shared/SchemaTypes'
import { PoolDynamicOverviewCTO } from '@shared/sharedTypes'
import { useParams } from 'react-router-dom'
import DynamicOffchainDepositForm from './DynamicOffchainDepositForm'
import DynamicTransferDepositForm from './DynamicTransferDepositForm'

export const DynamicDepositsTab = (props: { poolAddr: EthAddr }) => {
  const poolAddr = (props.poolAddr ? props.poolAddr : useParams().poolAddr) as EthAddr

  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    userConnection: { roleEnum },
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()
  const [poolBalance, setPoolBalance] = React.useState<bigint>(0n)

  const poolDynamicOverviewCTO: PoolDynamicOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO]
  ) as PoolDynamicOverviewCTO

  const [depositActivityCTOs, setDepositActivityCTOs] = useState<DynamicDepositActivityCTO[]>([])
  async function fetchDepositData() {
    logOT.debug(
      'fetchDepositData',
      providerReadOnly && poolAddr && poolDynamicOverviewCTO && serverApiBaseUrl && restAPIKey
    )
    if (providerReadOnly && poolAddr && poolDynamicOverviewCTO && serverApiBaseUrl && restAPIKey) {
      logOT.debug('fetchDepositData', poolAddr, serverApiBaseUrl, restAPIKey, 30)
      await scanPoolsForEvents(serverApiBaseUrl, restAPIKey, poolAddr)
      const depositActivityCTOs = await fetchPoolDynamicDeposits(
        poolDynamicOverviewCTO,
        serverApiBaseUrl,
        restAPIKey,
        30
      )

      logOT.debug('depositActivityCTOs', depositActivityCTOs)
      setDepositActivityCTOs(depositActivityCTOs)

      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

      const _balance = await fetchBalance(poolAddr, poolDynamicOverviewCTO.liquidityAssetAddr, providerReadOnly)
      setPoolBalance(_balance)
    }
  }
  useEffect(() => {
    fetchDepositData().catch((e) => console.error('Error fetching deposit data', e))
  }, [providerReadOnly, poolDynamicOverviewCTO, poolAddr, serverApiBaseUrl && restAPIKey])
  return (
    <Box sx={{ paddingTop: 2 }}>
      <Box sx={{ display: 'inline', float: 'right' }}>
        <ButtonModal
          title={'OffChain Deposit'}
          modalCloseCallback={() => {
            logOT.info('modalCloseCallback 1')
            fetchDepositData()
          }}
          variant="primary"
          modalWidth={800}
        >
          <DynamicOffchainDepositForm poolAddr={poolAddr}></DynamicOffchainDepositForm>
        </ButtonModal>
      </Box>

      <SectionHeader>
        Direct Transfers to Pool - Pool Balance {poolBalance.toString()} {poolDynamicOverviewCTO.liquidityTokenSymbol}
      </SectionHeader>
      <Box sx={{ padding: 4, marginBottom: 4 }}>
        {/*  Table header */}

        <Grid container spacing={2} sx={{ borderBottom: `1px solid ${theme.palette.grey[300]}` }}>
          <Grid item xs={3}>
            <TableHeaderItem>Lender</TableHeaderItem>
          </Grid>
          <Grid item xs={3}>
            <TableHeaderItem>Deposit Amount</TableHeaderItem>
          </Grid>
          <Grid item xs={3}>
            <TableHeaderItem>Timestamp</TableHeaderItem>
          </Grid>
          <Grid item xs={1}>
            <TableHeaderItem>Transaction Hash</TableHeaderItem>
          </Grid>
          <Grid item xs={2}>
            <TableHeaderItem>Action</TableHeaderItem>
          </Grid>
        </Grid>
        {depositActivityCTOs.map((activity: DynamicDepositActivityCTO) => {
          return (
            <Grid
              container
              spacing={2}
              sx={{ borderBottomWidth: 1, borderBottomColor: theme.palette.divider, marginTop: 2 }}
              key={activity.txHash}
            >
              <Grid item xs={3}>
                <CompanyProfileAddressItem walletAddr={activity.lender} />
              </Grid>
              <Grid item xs={2}>
                <Typography
                  variant="bodyBold"
                  sx={{ textAlign: 'left', justifyContent: 'flex-start', wordBreak: 'break-word' }}
                >
                  {erc20WeiToCommaDecString(activity.assets)} {poolDynamicOverviewCTO.liquidityTokenSymbol}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ textAlign: 'center', justifyContent: 'center', wordBreak: 'break-word' }}>
                <Typography variant="bodyBold">{timestampToUTCDateTimeString(activity.timestamp)}</Typography>
              </Grid>
              <Grid item xs={2}>
                <AddressPill addr={activity.txHash} />
              </Grid>
              <Grid item xs={2}>
                {activity.state === DynamicDepositStatusEnum.CONTRACT ? (
                  <Typography variant="bodyBold">Contract deposit</Typography>
                ) : activity.state === DynamicDepositStatusEnum.OFFCHAIN ? (
                  <Typography variant="bodyBold">Offchain deposit</Typography>
                ) : activity.state === DynamicDepositStatusEnum.TRANSFER_ACCEPTED ? (
                  <Typography variant="bodyBold">Transfer Accepted</Typography>
                ) : activity.state === DynamicDepositStatusEnum.ACCOUNT_CREDIT ? (
                  <Typography variant="bodyBold">Account Credit</Typography>
                ) : activity.state === DynamicDepositStatusEnum.TRANSFER_PENDING ? (
                  <ButtonModal
                    title={'Accept Transfer Deposit'}
                    variant="primary"
                    modalCloseCallback={() => {
                      fetchDepositData()
                    }}
                    disabled={roleEnum !== RoleEnum.POOL_ADMIN}
                  >
                    <DynamicTransferDepositForm
                      lenderAddr={activity.lender}
                      activity={activity}
                      symbol={poolDynamicOverviewCTO.symbol}
                      liquidityTokenSymbol={poolDynamicOverviewCTO.liquidityTokenSymbol}
                    ></DynamicTransferDepositForm>
                  </ButtonModal>
                ) : (
                  <Typography variant="bodyBold">{activity.state}</Typography>
                )}
              </Grid>
            </Grid>
          )
        })}
        {depositActivityCTOs.length === 0 && (
          <Box sx={{ padding: 4 }}>
            <Typography variant="bodyMuted">No Deposit activity at this time.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}
